import { useEffect, useState } from 'react';
import { Row, Col, Button, Spinner } from 'reactstrap';
import { H3, H6, LI, UL } from '../../../../AbstractElements';
import TextField from '../../../common/TextField';
import Dropdown from '../../../common/DropDown';
import DecimalInput from '../../../common/DecimalInput';
import { LiabilitiesFormData, LiabilitiesFormErrors } from '../../Public/FormData';
import { useGetRequestQuery } from '../../../../Hooks/useGetRequestQuery';
import { getAllClientLiabilitiesByClientIdService, getClientLiabilitiesByIdService, insertOrUpdateClientLiabilitiesService, softDeleteClientLiabilitiesService } from '../../../../Services/PublicPageServices';
import { useMutationRequestQuery } from '../../../../Hooks/useMutationRequestQuery';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../ReduxToolkit/Store';
import { useLocation } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import DollarInput from '../../../common/DollarInput';
const customStyles = {
    head: {
        style: {
            fontWeight: '800',
            color: '#0000ff',
            fontSize: '14px'
        },
    },
};
export default function LiabilitiesOthers({ clientFactsFindData }: any) {
    const [formData, setFormData] = useState<any>(LiabilitiesFormData);
    const [assetsPropertyData, setAssetsPropertyData] = useState([]);
    const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
    const [formErrors, setFormErrors] = useState<any>(LiabilitiesFormErrors);
    const location = useLocation();
    const isMMPAssetsLiabilitiesRoute = location.pathname === '/MMPAssetsLiabilities';
    const userData = useSelector((state: RootState) => state.auth);

    const {
        data: assetsProperty,
        refetch: refetchAssetsProperty,
        isLoading: assetsPropertyLoading,
        isError: assetsPropertyIsError,
    } = useGetRequestQuery(
        ['getAllClientLiabilitiesByClientIdService', userData?.nameidentifier],
        () => getAllClientLiabilitiesByClientIdService(userData?.nameidentifier as any),
        {
            enabled: true, // enable query only if clientId is available
        }
    );

    const {
        data: assetsPropertyFormData,
        isLoading: assetsPropertyFormDataLoading,
        isError: assetsPropertyFormDataIsError,
    } = useGetRequestQuery(
        ["getClientLiabilitiesByIdService", selectedRowId],
        () => getClientLiabilitiesByIdService(selectedRowId),
        {
            enabled: !!selectedRowId,
        }
    );

    useEffect(() => {
        if (assetsProperty?.data && !assetsPropertyIsError && !assetsPropertyLoading) {
            setAssetsPropertyData(assetsProperty?.data);
        }
    }, [assetsProperty, assetsPropertyIsError, assetsPropertyLoading]);


    useEffect(() => {
        if (assetsPropertyFormData?.data && !assetsPropertyFormDataIsError && !assetsPropertyFormDataLoading) {
            if (assetsPropertyFormData?.data?.id) {
                setSelectedRowId(assetsPropertyFormData?.data?.id)
            }
            setFormData({
                liabilitiesLiabilityType: assetsPropertyFormData?.data?.liabilityType || "",
                liabilitiesLendersName: assetsPropertyFormData?.data?.lendersName || "",
                liabilitiesCurrentBalance: assetsPropertyFormData?.data?.currentBalance || "",
                liabilitiesCurrentLimit: assetsPropertyFormData?.data?.currentLimit || "",
                liabilitiesInterestRate: assetsPropertyFormData?.data?.liabilitiesInterestRate || "",
                liabilitiesRepayment: assetsPropertyFormData?.data?.repayment || "",
                liabilitiesRepaymentDLL: assetsPropertyFormData?.data?.frequency || "",
            })
        }
    }, [assetsPropertyFormData, assetsPropertyFormDataIsError, assetsPropertyFormDataLoading]);


    const { mutate: updateClientAssets, isPending: isUpdateClientAssetsPending } = useMutationRequestQuery(
        'insertOrUpdateClientLiabilitiesService',
        insertOrUpdateClientLiabilitiesService,
        {
            onSuccess: (data: any) => {
                toast.success("Liabilities information saved or updated successfully.");
                setFormData(LiabilitiesFormData)
                setSelectedRowId(null)
                refetchAssetsProperty()
            },
            onError: (error: any) => {
                toast.error("Failed to submit Liabilities information");
                console.error(error);
            },
        }
    );

    const {
        mutate: deleteClientAssets,
        isPending: isDeleteClientAssetsPending,
    } = useMutationRequestQuery(
        'softDeleteClientLiabilitiesService',
        softDeleteClientLiabilitiesService,
        {
            onSuccess: (data: any) => {
                toast.success("Liabilitie deleted successfully.");
                refetchAssetsProperty()
                setSelectedRowId(null)
            },
            onError: (error: any) => {
                toast.error("Failed to delete property.");
                console.error(error);
            },
        }
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validate if all fields are filled
        const requiredFields = [
            'liabilitiesLiabilityType',
            'liabilitiesLendersName',
            'liabilitiesCurrentBalance',
            'liabilitiesCurrentLimit',
            'liabilitiesInterestRate',
            'liabilitiesRepayment',
            'liabilitiesRepaymentDLL',
        ];

        for (let field of requiredFields) {
            if (!formData[field] || formData[field].trim() === "") {
                toast.error("Please fill all Liabilities fields.");
                return;
            }
        }

        // If all fields are valid, proceed with submission
        updateClientAssets({
            id: selectedRowId,
            clientId: userData?.nameidentifier,
            liabilityType: formData.liabilitiesLiabilityType,
            lendersName: formData.liabilitiesLendersName,
            currentBalance: formData.liabilitiesCurrentBalance,
            currentLimit: formData.liabilitiesCurrentLimit,
            repayment: formData.liabilitiesRepayment,
            frequency: formData.liabilitiesRepaymentDLL,
            liabilitiesInterestRate: formData.liabilitiesInterestRate,
        });
    };


    const handleValueChange = (name: string, value: string) => {
        setFormData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
    };
    const handleErrorChange = (name: string, error: boolean) => {
        setFormErrors((prevErrors: any) => ({
            ...prevErrors,
            [name]: error,
        }));
    };


    const splitDropdownValues = (dropdownValues: string) => {
        return dropdownValues ? dropdownValues.split(',').map(value => value.trim()) : [];
    };

    const handleEditClick = (id: number) => {
        setSelectedRowId(id);
    };

    const handleDeleteClick = (id: number) => {
        deleteClientAssets(id)
    };

    const zeroConfigurationTableColumns = [
        {
            name: "Action",
            cell: (row: any) => (
                <UL className="action simple-list flex-row">
                    <LI style={{ 'cursor': 'pointer' }} className="edit"><a onClick={() => { handleEditClick(row.id) }}><i className="icon-pencil-alt" /></a></LI>
                    <LI style={{ 'cursor': 'pointer' }} className="delete"><a onClick={() => { handleDeleteClick(row.id) }}><i className="icon-trash" /></a></LI>
                </UL>
            ),
            style: {
                minWidth: '9rem', // Home Address column width set to 9rem
            }
        },
        {
            name: "Liability Type",
            selector: (row: any) => row["liabilityType"],
            sortable: false, style: {
                minWidth: '9rem', // Home Address column width set to 9rem
            }
        },
        {
            name: "Lender's Name",
            selector: (row: any) => row["lendersName"],
            sortable: false, style: {
                minWidth: '9rem', // Home Address column width set to 9rem
            }
        },
        {
            name: "Current Balance",
            selector: (row: any) => row["currentBalance"],
            sortable: false, style: {
                minWidth: '9rem', // Home Address column width set to 9rem
            }
        },
        {
            name: "Current Limit",
            selector: (row: any) => row["currentLimit"],
            sortable: false, style: {
                minWidth: '9rem', // Home Address column width set to 9rem
            }
        },
        {
            name: "Repayment",
            selector: (row: any) => row["repayment"],
            sortable: false, style: {
                minWidth: '9rem', // Home Address column width set to 9rem
            }
        },
        {
            name: "Frequency",
            selector: (row: any) => row["frequency"],
            sortable: false, style: {
                minWidth: '9rem', // Home Address column width set to 9rem
            }
        },
    ];

    const handleCancel = () => {
        setSelectedRowId(null)
        setFormData(LiabilitiesFormData)
    };

    return (
        <div className={isMMPAssetsLiabilitiesRoute ? 'mmp-assets-liabilities container-fluid' : 'container-fluid'}>
            <div className="table-responsive h-100 py-4">
                <section className='bg-white'>

                    <div className="scrollable-table-container">
                        <table className="scrollable-table" style={{ marginLeft: '1%', marginTop: '3%' }}>
                            <div className="d-flex align-items-center">
                                <Col xs={12} className='d-flex'>
                                    <H3 className="heading-color f-w-900 text-left my-2">
                                        Liabilities - Other</H3>
                                    {/* <Button style={{ marginLeft: '10px' }} color="primary" disabled={isAssetsAndLiabilitiesLoading} onClick={handleHover}>
                                    {isAssetsAndLiabilitiesLoading ? "Loading . . ." : "Save"}
                                </Button> */}
                                </Col>
                            </div>
                            {assetsPropertyLoading ? (
                                <div
                                    style={{
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        padding: '15px',
                                        backgroundColor: '#fff',
                                        textAlign: 'center',
                                    }}
                                    className='mt-4'
                                >
                                    <Spinner color="primary" />
                                </div>
                            ) : assetsPropertyData.length !== 0 ? (
                                <div
                                    style={{
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        padding: '5px',
                                        backgroundColor: '#fff',
                                    }}
                                    className='mt-4'
                                >
                                    <DataTable
                                        title=""
                                        columns={zeroConfigurationTableColumns}
                                        data={assetsPropertyData}
                                        responsive
                                        customStyles={customStyles}
                                    />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        padding: '15px',
                                        backgroundColor: '#fff',
                                        textAlign: 'center',
                                    }}
                                    className='mt-4'
                                >
                                    <h6>No liabilities have been added yet.</h6>
                                </div>
                            )}

                            <Row className='mt-4' >
                                <div className='d-flex'>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '11%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Liability Type </H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '11%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Lenders Name </H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '9%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Current Balance
                                        </H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '9%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Current Limit
                                        </H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '9%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Interest Rate
                                        </H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '9%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Repayment
                                        </H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '10%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Frequency</H6>
                                    </Col>
                                </div>
                                <div className='d-flex item-center mt-2'>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '11%', paddingInline: '2px' }}>
                                        <Dropdown
                                            label=""
                                            name="liabilitiesLiabilityType"
                                            value={formData.liabilitiesLiabilityType}
                                            setValue={handleValueChange}
                                            options={splitDropdownValues(clientFactsFindData ? clientFactsFindData[15].dropDownValues : '')}
                                            // required={true}
                                            setError={handleErrorChange}
                                            errorMessage="Liability type is required"
                                            error={formErrors.liabilitiesLiabilityType}
                                            tabIndex={75}

                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '11%', paddingInline: '2px' }}>
                                        <TextField
                                            label=""
                                            type="text"
                                            name="liabilitiesLendersName"
                                            value={formData.liabilitiesLendersName}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            // required={true}
                                            setError={handleErrorChange}
                                            errorMessage="Lender's name is required"
                                            error={formErrors.liabilitiesLendersName}
                                            maxChars={15}
                                            tabIndex={76}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '9%', paddingInline: '2px' }}>
                                        <DollarInput
                                            label=""
                                            name="liabilitiesCurrentBalance"
                                            value={formData.liabilitiesCurrentBalance}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            // required={true}
                                            limit={6}
                                            setError={handleErrorChange}
                                            errorMessage="Current balance is required"
                                            error={formErrors.liabilitiesCurrentBalance}
                                            tabIndex={77}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '9%', paddingInline: '2px' }}>
                                        <DollarInput
                                            label=""
                                            name="liabilitiesCurrentLimit"
                                            value={formData.liabilitiesCurrentLimit}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            // required={true}
                                            limit={6}
                                            setError={handleErrorChange}
                                            errorMessage="Current limit is required"
                                            error={formErrors.liabilitiesCurrentLimit}
                                            tabIndex={78}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '9%', paddingInline: '2px' }}>
                                        <DecimalInput
                                            label=""
                                            name="liabilitiesInterestRate"
                                            value={formData.liabilitiesInterestRate}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            // required={true}
                                            limit={4}
                                            setError={handleErrorChange}
                                            errorMessage="Interest rate is required"
                                            error={formErrors.liabilitiesInterestRate}
                                            tabIndex={79}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '9%', paddingInline: '2px' }}>
                                        <DollarInput
                                            label=""
                                            name="liabilitiesRepayment"
                                            value={formData.liabilitiesRepayment}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            // required={true}
                                            limit={6}
                                            setError={handleErrorChange}
                                            errorMessage="Repayment amount is required"
                                            error={formErrors.liabilitiesRepayment}
                                            tabIndex={80}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-liabilities' style={{ width: '10%', paddingInline: '2px' }}>
                                        <Dropdown
                                            label=""
                                            name="liabilitiesRepaymentDLL"
                                            value={formData.liabilitiesRepaymentDLL}
                                            setValue={handleValueChange}
                                            options={splitDropdownValues(clientFactsFindData ? clientFactsFindData[11].dropDownValues : '')}
                                            // required={true}
                                            setError={handleErrorChange}
                                            errorMessage="Repayment DLL is optional"
                                            error={formErrors.liabilitiesRepaymentDLL}
                                            tabIndex={81}
                                        />
                                    </Col>
                                </div>
                            </Row>
                        </table>
                        <Button
                            disabled={isUpdateClientAssetsPending}
                            type="button"
                            color="primary"
                            onClick={handleSubmit}
                            style={{ fontSize: '16px', marginBottom: '7px' }}
                        >
                            {isUpdateClientAssetsPending ? 'Loading . . .' : selectedRowId ? "Update Liability" : "Add New Liability"}
                        </Button>
                        {!selectedRowId && <Button
                            type="button"
                            color="secondary"
                            onClick={handleCancel}
                            style={{ marginBottom: '7px', fontSize: "16px", marginLeft: '20px', width: '180px' }}
                        >
                            Cancel
                        </Button>}
                    </div>
                </section>
            </div>
        </div>
    );
}
